import React, { useState, useEffect } from 'react';
import { Column, Table as Table1 } from 'react-virtualized';
import 'react-virtualized/styles.css';
import './table.css';
import {
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from '@material-ui/core';
import { CSVLink } from 'react-csv';
import { Close, Edit } from '@material-ui/icons';
import {
  includes,
  filter,
  forEach,
  find,
  reverse,
  sortBy,
  groupBy,
} from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import UploadedDataDialog from './UploadedDataDialog';

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
});

const prepareHeaders = scoreData => {
  return filter(
    Object.keys(scoreData[0]),
    key => !includes(['HCP Name', 'HCP Number', 'hcpScore'], key)
  );
};

function ScoreTable({
  areaParameters,
  uploadedData,
  isScoreTableOpen,
  toggleScoreTable,
  areaName,
  // fileName,
  therapeuticArea,
}) {
  const classes = useStyles();
  const [extraColumns, toggleExtraColumns] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [calculationData, setCalculationData] = useState(uploadedData);
  const [dataHeaders, setDataHeaders] = useState(prepareHeaders(uploadedData));
  const [tableHeaders, setTableHeaders] = useState([]);
  const [downloadableScoreData, setDownloadableScoreData] = useState([]);
  const [showScore, toggleShowScore] = useState(false);
  const [scoreData, setScoreData] = useState([]);
  const [updateDialog, toggleUpdateDialog] = useState(false);
  const [rowIndexToUpdate, setRowIndexToUpdate] = useState(-1);
  const [rowDataToUpdate, setRowDataToUpdate] = useState(null);
  const [recordsToDownload, setRecordsToDownload] = useState(scoreData.length);

  useEffect(() => {
    if (scoreData && scoreData[0]) {
      setHeaders(Object.keys(scoreData[0]));
    }
  }, [scoreData]);

  const sortScoreList = (list, sortVal) => {
    if (sortVal === 'hcpScore') {
      const areaParams = areaParameters.sort((a, b) => {
        if (a.importance > b.importance) return -1;
        if (a.importance < b.importance) return 1;
        if (a.parameter.name > b.parameter.name) return 1;
        if (a.parameter.name < b.parameter.name) return -1;
      });
      const groupedAreaParams = groupBy(areaParameters, 'importance');
      const groupedAreaParamsKeys = Object.keys(groupedAreaParams).sort(
        (a, b) => b - a
      );
      const sortedList = list.sort((a, b) => {
        if (a.hcpScore > b.hcpScore) return -1;
        if (a.hcpScore < b.hcpScore) return 1;
        if (a.hcpScore - b.hcpScore === 0) {
          for (let i = 0; i < groupedAreaParamsKeys.length; i++) {
            const key = groupedAreaParamsKeys[i];
            let aScore = 0;
            let bScore = 0;
            for (let j = 0; j < groupedAreaParams[key].length; j++) {
              aScore += a[groupedAreaParams[key][j].parameter.name + '_score'];
              bScore += b[groupedAreaParams[key][j].parameter.name + '_score'];
            }
            if (aScore > bScore) return -1;
            if (aScore < bScore) return 1;
          }

          // for (let i = 0; i < areaParams.length; i++) {
          //   if (
          //     a[`${areaParams[i].parameter.name}_score`] >
          //     b[`${areaParams[i].parameter.name}_score`]
          //   )
          //     return -1;
          //   if (
          //     a[`${areaParams[i].parameter.name}_score`] <
          //     b[`${areaParams[i].parameter.name}_score`]
          //   )
          //     return 1;
          // }
          if (a['HCP Name'] > b['HCP Name']) return 1;
          if (a['HCP Name'] < b['HCP Name']) return -1;
        }
      });
      setScoreData(sortedList);
    } else {
      setScoreData(sortBy(list, score => score[sortVal]));
    }
  };

  const calculate = () => {
    const hcpScore = calculationData.map((d, index) => {
      const obj = {
        hcpScore: 0,
        ...d,
      };
      forEach(Object.keys(d), key => {
        const areaParameter = find(
          therapeuticArea.areaParameters,
          ap => ap.parameter.name === key
        );
        if (areaParameter) {
          obj[areaParameter.parameter.name + '_score'] =
            areaParameter.importance * d[key];
          obj.hcpScore += areaParameter.importance * d[key];
        }
      });
      return obj;
    });
    setTableHeaders(prepareHeaders(hcpScore));
    sortScoreList(hcpScore, 'hcpScore');
    setRecordsToDownload(hcpScore.length);
    toggleShowScore(true);
  };

  const onFileDownload = () => {
    if (!extraColumns) {
      const dataToDownload = scoreData.map(item => {
        return {
          'HCP Number': item['HCP Number'],
          'HCP Name': item['HCP Name'],
          hcpScore: item.hcpScore,
        };
      });
      setDownloadableScoreData(dataToDownload.slice(0, recordsToDownload));
      setHeaders(Object.keys(dataToDownload[0]));
    } else {
      setDownloadableScoreData(scoreData.slice(0, recordsToDownload));
      setHeaders(Object.keys(scoreData[0]));
    }
  };

  const editUploadedData = (index, data) => {
    setRowIndexToUpdate(index);
    setRowDataToUpdate(data);
    toggleUpdateDialog(true);
  };

  const editRowData = (index, rowData) => {
    const calculationDataCopy = calculationData.slice();
    calculationDataCopy[index] = rowData;
    setCalculationData(calculationDataCopy);
    setRowDataToUpdate(null);
    setRowIndexToUpdate(-1);
    toggleUpdateDialog(false);
  };

  const cellRenderer = ({
    cellData,
    columnData,
    columnIndex,
    dataKey,
    isScrolling,
    rowData,
    rowIndex,
  }) => {
    return (
      <IconButton
        variant="outlined"
        onClick={() => editUploadedData(rowIndex, rowData)}
        color="primary"
      >
        <Edit />
      </IconButton>
    );
  };

  const cellRenderer2 = ({
    cellData,
    columnData,
    columnIndex,
    dataKey,
    isScrolling,
    rowData,
    rowIndex,
  }) => {
    return rowIndex + 1;
  };

  const onReset = () => {
    toggleShowScore(false);
  };

  return (
    <React.Fragment>
      {updateDialog && rowDataToUpdate ? (
        <UploadedDataDialog
          isModalOpen={updateDialog}
          toggleModal={toggleUpdateDialog}
          dataToEdit={rowDataToUpdate}
          rowIndexToUpdate={rowIndexToUpdate}
          editRowData={editRowData}
        />
      ) : null}
      <Dialog open={isScoreTableOpen} fullScreen>
        <DialogTitle disableTypography={true}>
          <Grid container justify="space-between">
            <Typography>{`HCP Score for ${areaName}`}</Typography>
            <IconButton
              variant="outlined"
              onClick={() => toggleScoreTable(false)}
              color="secondary"
            >
              <Close />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            style={{
              flexGrow: 1,
            }}
            justify="center"
          >
            <Grid
              container
              alignItems="center"
              justify="flex-end"
              style={{ marginBottom: 16 }}
            >
              {/* <Typography
                style={{
                  flexGrow: 1,
                }}
              >{`Data source: ${fileName}`}</Typography> */}
              {showScore ? (
                <React.Fragment>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={extraColumns}
                        onChange={e => toggleExtraColumns(!extraColumns)}
                        color="primary"
                      />
                    }
                    label="Show Details"
                  />
                  <Typography
                    style={{ marginRight: 16 }}
                  >{`Total records: ${scoreData.length}`}</Typography>
                  <TextField
                    id="recordsToDownload"
                    label="No of records to download"
                    variant="outlined"
                    value={recordsToDownload}
                    onChange={e => setRecordsToDownload(e.target.value)}
                    required
                    error={
                      !recordsToDownload ||
                      recordsToDownload == 0 ||
                      recordsToDownload > scoreData.length
                    }
                    type="number"
                    style={{ marginRight: 16 }}
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onReset}
                    style={{ marginRight: 16 }}
                  >
                    Reset
                  </Button>
                  <Button
                    disabled={
                      !recordsToDownload ||
                      recordsToDownload == 0 ||
                      recordsToDownload > scoreData.length
                    }
                    variant="outlined"
                    color="primary"
                  >
                    <CSVLink
                      headers={headers}
                      data={downloadableScoreData}
                      filename={`${areaName}_score.csv`}
                      onClick={onFileDownload}
                      style={{ color: 'inherit', textDecoration: 'inherit' }}
                    >
                      Download
                    </CSVLink>
                  </Button>
                </React.Fragment>
              ) : (
                <Button color="primary" variant="outlined" onClick={calculate}>
                  Calculate
                </Button>
              )}
            </Grid>
            {showScore ? (
              <Table1
                width={screen.width * (90 / 100)}
                height={screen.height * (70 / 100)}
                headerHeight={20}
                rowHeight={100}
                rowCount={scoreData.length}
                rowGetter={({ index }) => scoreData[index]}
              >
                <Column
                  width={50}
                  label="Sr No"
                  dataKey={'HCP Number'}
                  cellRenderer={cellRenderer2}
                />
                <Column width={200} label="HCP Name" dataKey="HCP Name" />
                <Column width={200} label="HCP Number" dataKey="HCP Number" />
                <Column width={200} label="Score" dataKey="hcpScore" />
                {extraColumns
                  ? tableHeaders.map(key => (
                      <Column width={200} label={key} dataKey={key} />
                    ))
                  : null}
              </Table1>
            ) : (
              <Table>
                <TableContainer>
                  <Table1
                    width={screen.width * (90 / 100)}
                    height={screen.height * (70 / 100)}
                    headerHeight={20}
                    rowHeight={100}
                    rowCount={calculationData.length}
                    rowGetter={({ index }) => calculationData[index]}
                  >
                    <Column
                      width={200}
                      label="Sr No"
                      dataKey={'HCP Number'}
                      cellRenderer={cellRenderer2}
                    />
                    <Column width={200} label="HCP Name" dataKey="HCP Name" />
                    <Column
                      width={200}
                      label="HCP Number"
                      dataKey="HCP Number"
                    />
                    {dataHeaders.map(key => (
                      <Column width={200} label={key} dataKey={key} />
                    ))}
                    <Column
                      width={200}
                      label="Edit"
                      dataKey="HCP Name"
                      cellRenderer={cellRenderer}
                    />
                  </Table1>
                </TableContainer>
              </Table>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

export default ScoreTable;



// WEBPACK FOOTER //
// ./src/ScoreTable.js